/// <reference types="google.maps" />

export default class GoogleMap {
    // Track the currently open info window so that we can close it when a new one opens.
    private currentInfoWindow: google.maps.InfoWindow | null = null;

    constructor() {
        const mapsContainers = document.querySelectorAll(".maps");
        if (mapsContainers.length === 0) {
            return;
        }

        this.waitForGoogleMaps(() => {
            this.initializeMaps();
        });
    }

    /**
     * Polls for the Google Maps API until it's available.
     * @param callback Called once the API is loaded.
     * @param interval Time in milliseconds between checks.
     * @param maxAttempts Maximum number of attempts before giving up.
     */
    private waitForGoogleMaps(callback: () => void, interval = 100, maxAttempts = 50): void {
        let attempts = 0;
        const check = () => {
            if ((window as any).google && (window as any).google.maps) {
                callback();
            } else {
                attempts++;
                if (attempts < maxAttempts) {
                    setTimeout(check, interval);
                } else {
                    console.error("Google Maps API failed to load after waiting.");
                }
            }
        };
        check();
    }

    private initializeMaps(): void {
        const mapsContainers = document.querySelectorAll(".maps");
        if (mapsContainers.length === 0) {
            return;
        }

        mapsContainers.forEach((mapElement) => {
            this.initMap(mapElement as HTMLElement);
        });
    }

    private initMap(mapElement: HTMLElement): void {
        const zoom = parseInt(mapElement.getAttribute("data-zoom") || "10", 10);
        const markers = mapElement.querySelectorAll(".marker");

        if (markers.length === 0) {
            console.error("No markers found in the maps container.");
            return;
        }

        // Center the map using the first marker's coordinates
        const firstMarker = markers[0] as HTMLElement;
        const defaultLat = parseFloat(firstMarker.getAttribute("data-lat") || "0");
        const defaultLng = parseFloat(firstMarker.getAttribute("data-lng") || "0");

        const map = new google.maps.Map(mapElement, {
            center: { lat: defaultLat, lng: defaultLng },
            zoom: zoom,
            styles: [
                {
                    "featureType": "landscape.man_made",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#f7f1df",
                        },
                    ],
                },
                {
                    "featureType": "landscape.natural",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#d0e3b4",
                        },
                    ],
                },
                {
                    "featureType": "landscape.natural.terrain",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "visibility": "off",
                        },
                    ],
                },
                {
                    "featureType": "poi",
                    "elementType": "labels",
                    "stylers": [
                        {
                            "visibility": "off",
                        },
                    ],
                },
                {
                    "featureType": "poi.business",
                    "elementType": "all",
                    "stylers": [
                        {
                            "visibility": "off",
                        },
                    ],
                },
                {
                    "featureType": "poi.medical",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#fbd3da",
                        },
                    ],
                },
                {
                    "featureType": "poi.park",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#bde6ab",
                        },
                    ],
                },
                {
                    "featureType": "road",
                    "elementType": "geometry.stroke",
                    "stylers": [
                        {
                            "visibility": "off",
                        },
                    ],
                },
                {
                    "featureType": "road",
                    "elementType": "labels",
                    "stylers": [
                        {
                            "visibility": "off",
                        },
                    ],
                },
                {
                    "featureType": "road.highway",
                    "elementType": "geometry.fill",
                    "stylers": [
                        {
                            "color": "#ffe15f",
                        },
                    ],
                },
                {
                    "featureType": "road.highway",
                    "elementType": "geometry.stroke",
                    "stylers": [
                        {
                            "color": "#efd151",
                        },
                    ],
                },
                {
                    "featureType": "road.arterial",
                    "elementType": "geometry.fill",
                    "stylers": [
                        {
                            "color": "#ffffff",
                        },
                    ],
                },
                {
                    "featureType": "road.local",
                    "elementType": "geometry.fill",
                    "stylers": [
                        {
                            "color": "black",
                        },
                    ],
                },
                {
                    "featureType": "transit.station.airport",
                    "elementType": "geometry.fill",
                    "stylers": [
                        {
                            "color": "#cfb2db",
                        },
                    ],
                },
                {
                    "featureType": "water",
                    "elementType": "geometry",
                    "stylers": [
                        {
                            "color": "#a2daf2",
                        },
                    ],
                },
            ],
        });

        markers.forEach((markerElement) => {
            this.addMarker(map, markerElement as HTMLElement);
        });
    }

    private addMarker(map: google.maps.Map, markerElement: HTMLElement): void {
        const lat = parseFloat(markerElement.getAttribute("data-lat") || "0");
        const lng = parseFloat(markerElement.getAttribute("data-lng") || "0");
        const icon = markerElement.getAttribute("data-marker-icon") || undefined;
        const infoContent = markerElement.innerHTML.trim();

        const marker = new google.maps.Marker({
            position: { lat, lng },
            map,
            icon: icon,
        });

        if (infoContent) {
            const infoWindow = new google.maps.InfoWindow({
                content: infoContent,
            });

            marker.addListener("click", () => {
                // Close any currently open info window
                if (this.currentInfoWindow) {
                    this.currentInfoWindow.close();
                }
                infoWindow.open(map, marker);
                this.currentInfoWindow = infoWindow;
            });
        }
    }
}
